import React from 'react';
import styled from 'styled-components';
import { config } from '../../config';

const PolicyContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px 40px;
  
  @media (max-width: 768px) {
    padding: 60px 20px 40px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  color: #333;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  margin-bottom: 32px;
  font-size: 14px;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: #333;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #555;
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 16px 0 16px 24px;
  
  li {
    font-size: 16px;
    line-height: 1.8;
    color: #555;
    margin-bottom: 8px;
  }
`;

const TermsAndConditions = () => {
  return (
    <PolicyContainer>
      <Title>Terms and Conditions</Title>
      <LastUpdated>Last Updated: December 28, 2025</LastUpdated>

      <Section>
        <Paragraph>
          This document is an electronic record in terms of Information Technology Act, 2000 and rules 
          thereunder as applicable. This electronic record is generated by a computer system and does not 
          require any physical or digital signatures.
        </Paragraph>
        <Paragraph>
          This document is published in accordance with the provisions of Rule 3(1) of the Information 
          Technology (Intermediaries Guidelines) Rules, 2011. The Platform is owned by {config.company.name}, 
          with its registered office at {config.company.address}.
        </Paragraph>
        <Paragraph>
          By accessing or using our website, mobile application, services, or products (collectively, the "Platform"), 
          you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our Platform.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>1. Definitions</SectionTitle>
        <Paragraph>
          <strong>"Platform"</strong> means the {config.company.name} website ({config.company.website}), 
          mobile applications, and all related services.
        </Paragraph>
        <Paragraph>
          <strong>"Services"</strong> includes career training programs, educational courses, live online classes, 
          SaaS products (including restaurant management software), and any other services offered by us.
        </Paragraph>
        <Paragraph>
          <strong>"User", "You", "Your"</strong> refers to any person or entity accessing or using our Platform or Services.
        </Paragraph>
        <Paragraph>
          <strong>"Content"</strong> means all materials including text, graphics, images, software, course materials, 
          and live class sessions provided through our Platform.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>2. Acceptance of Terms</SectionTitle>
        <Paragraph>
          By accessing, browsing, or using the Platform, you indicate your agreement to all the terms and conditions 
          under these Terms of Use. Please read these Terms carefully before proceeding. These Terms can be modified 
          at any time without prior notice. It is your responsibility to periodically review these Terms to stay 
          informed of updates.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>3. Services Offered</SectionTitle>
        <Paragraph>
          {config.company.name} offers a range of technology products and services including but not limited to:
        </Paragraph>
        <List>
          <li><strong>Educational Services:</strong> Career development programs, training courses, workshops, 
          mentorship, and placement assistance delivered through various formats including live classes, 
          recorded sessions, or hybrid models</li>
          <li><strong>Software Products (SaaS):</strong> Cloud-based software applications, mobile applications, 
          web applications, and enterprise solutions provided on a subscription or licensing basis</li>
          <li><strong>Digital Products:</strong> E-books, study materials, templates, tools, and downloadable content</li>
          <li><strong>Consulting Services:</strong> Technical consulting, career guidance, business solutions, 
          and professional advisory services</li>
          <li><strong>Physical Products:</strong> Hardware, IoT devices, electronics, books, or other tangible 
          products (where applicable)</li>
          <li><strong>Custom Development:</strong> Bespoke software development, website creation, mobile app 
          development, and tailored technology solutions</li>
          <li><strong>Support Services:</strong> Technical support, customer service, maintenance, and ongoing assistance</li>
        </List>
        <Paragraph>
          The specific terms for each product or service category may vary and will be communicated at the time 
          of purchase or enrollment. We reserve the right to add, modify, suspend, or discontinue any Services 
          at any time with or without notice.
        </Paragraph>
        <Paragraph>
          <em>Note: For product-specific terms and policies, please refer to the respective product documentation 
          or contact our support team.</em>
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>4. User Registration and Account</SectionTitle>
        <Paragraph>
          To access certain Services, you must create an account. You agree to:
        </Paragraph>
        <List>
          <li>Provide true, accurate, current, and complete information during registration</li>
          <li>Maintain and promptly update your account information</li>
          <li>Maintain the security and confidentiality of your password and account credentials</li>
          <li>Notify us immediately of any unauthorized use of your account</li>
          <li>Accept responsibility for all activities that occur under your account</li>
          <li>Not share your account credentials with any third party</li>
        </List>
        <Paragraph>
          You are solely responsible for all acts done through your registered account on the Platform.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>5. Payment Terms</SectionTitle>
        <Paragraph>
          All payments are subject to the following terms:
        </Paragraph>
        <List>
          <li>All prices are listed in Indian Rupees (INR) and are inclusive of applicable taxes</li>
          <li>Payments must be completed through our authorized payment gateway (PhonePe)</li>
          <li>Payment must be received in full before accessing Services</li>
          <li>We reserve the right to change pricing with 30 days advance notice</li>
          <li>Special offer prices are valid only for the specified period</li>
          <li>Refunds are subject to our Refund Policy</li>
          <li>All fees are non-refundable except as expressly stated in our Refund Policy</li>
          <li>You agree to pay all charges associated with availing the Services</li>
        </List>
        <Paragraph>
          Upon initiating a transaction, you are entering into a legally binding and enforceable contract with 
          {config.company.name} for the Services.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>6. Access and Usage Terms</SectionTitle>
        <Paragraph>
          Access and usage terms vary by product type:
        </Paragraph>
        <Paragraph>
          <strong>For Educational Services:</strong>
        </Paragraph>
        <List>
          <li>Access is provided based on the format specified (live sessions, recorded content, or hybrid)</li>
          <li>Schedules will be communicated in advance where applicable</li>
          <li>Study materials and resources will be provided as part of enrolled programs</li>
          <li>Access duration varies by program and will be specified at enrollment</li>
          <li>You may not record, distribute, share, or commercially exploit any content</li>
          <li>Attendance requirements and participation guidelines will be provided for each program</li>
        </List>
        <Paragraph>
          <strong>For Software Products (SaaS):</strong>
        </Paragraph>
        <List>
          <li>Access is provided on a subscription or licensing basis as per the selected plan</li>
          <li>Service availability is subject to our Service Level Agreement (target 99% uptime)</li>
          <li>You are responsible for data backups and security of your account</li>
          <li>Scheduled maintenance will be performed with advance notice where possible</li>
          <li>Usage limits, storage quotas, and feature access depend on your subscription tier</li>
          <li>You may not reverse engineer, decompile, or attempt to extract source code</li>
          <li>Resale or redistribution of software is strictly prohibited</li>
        </List>
        <Paragraph>
          <strong>For Digital Products:</strong>
        </Paragraph>
        <List>
          <li>Downloads are provided for personal or permitted business use only</li>
          <li>Digital content may not be resold, redistributed, or shared</li>
          <li>You may make backup copies for personal use</li>
          <li>Access credentials are non-transferable</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>7. Refunds, Returns, and Cancellations</SectionTitle>
        <Paragraph>
          Our refund and return policies vary by product type. Please refer to our detailed Refund Policy 
          and Return Policy for complete information.
        </Paragraph>
        <Paragraph>
          <strong>General Refund Guidelines:</strong>
        </Paragraph>
        <List>
          <li>Refunds are subject to our Refund Policy and specific product terms</li>
          <li>Digital products and started services are generally non-refundable</li>
          <li>Educational programs may have specific refund windows</li>
          <li>SaaS subscriptions follow the terms specified in the Refund Policy</li>
          <li>Physical products are subject to our Return and Shipping Policy</li>
          <li>Refund processing may take 7-14 business days</li>
        </List>
        <Paragraph>
          <strong>For Physical Product Returns (where applicable):</strong>
        </Paragraph>
        <List>
          <li>Return requests must be initiated within the specified return window</li>
          <li>Products must be unused, in original packaging with all accessories</li>
          <li>Return shipping costs may be borne by the customer unless the product is defective</li>
          <li>Restocking fees may apply as specified in the Return Policy</li>
          <li>Damaged, defective, or wrong items will be replaced or refunded at no cost</li>
          <li>Custom or personalized items may not be eligible for return</li>
        </List>
        <Paragraph>
          <strong>Cancellation Policy:</strong>
        </Paragraph>
        <List>
          <li>Order cancellations must be requested before product shipment or service activation</li>
          <li>SaaS subscriptions can be cancelled as per the subscription terms</li>
          <li>Educational program cancellations follow the specific program's policy</li>
          <li>Some services may have cancellation fees or penalties</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>8. Intellectual Property Rights</SectionTitle>
        <Paragraph>
          All content, features, functionality, software, and materials on our Platform are owned by 
          {config.company.name} and are protected by Indian and international copyright, trademark, patent, 
          trade secret, and other intellectual property laws.
        </Paragraph>
        <Paragraph>
          You are granted a limited, non-exclusive, non-transferable license to:
        </Paragraph>
        <List>
      <Section>
        <SectionTitle>10. Disclaimers and Warranties</SectionTitle>
        <Paragraph>
          THE PLATFORM AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, 
          EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A 
          PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
        </Paragraph>
        <Paragraph>
          We do not warrant that:
        </Paragraph>
        <List>
          <li>The Platform will be uninterrupted, secure, or error-free</li>
          <li>The Services will meet your specific requirements or expectations</li>
          <li>Educational programs will guarantee specific career outcomes, employment, or salary levels</li>
          <li>Software will be free from bugs, errors, or security vulnerabilities</li>
          <li>Information provided is always accurate, complete, or current</li>
          <li>Physical products (if any) will be free from defects (separate product warranty applies)</li>
          <li>Third-party integrations or services will function without interruption</li>
        </List>
        <Paragraph>
          Your use of the Platform and Services is solely at your own risk and discretion. 
          You are required to independently assess and ensure that the Services meet your requirements.
        </Paragraph>
        <Paragraph>
          <strong>Product-Specific Warranties:</strong> Specific products may come with limited warranties 
          as described in product documentation. Such warranties do not extend these general disclaimers.
        </Paragraph>
      </Section>
          <li>Use the Platform or Services for any unlawful purpose or illegal activity</li>
          <li>Violate any Indian or international laws or regulations</li>
          <li>Infringe upon intellectual property rights of {config.company.name} or any third party</li>
          <li>Upload viruses, malware, or any malicious code</li>
          <li>Attempt to gain unauthorized access to our systems, networks, or data</li>
          <li>Interfere with or disrupt the Platform or Services</li>
          <li>Harass, abuse, or harm other users or instructors</li>
          <li>Impersonate any person or entity</li>
          <li>Collect or store personal data of other users</li>
          <li>Use automated systems (bots, scrapers) to access the Platform</li>
          <li>Engage in any fraudulent activity</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>10. Disclaimers and Warranties</SectionTitle>
        <Paragraph>
          THE PLATFORM AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, 
          EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A 
          PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
        </Paragraph>
        <Paragraph>
          We do not warrant that:
        </Paragraph>
        <List>
          <li>The Platform will be uninterrupted, secure, or error-free</li>
          <li>The Services will meet your specific requirements or expectations</li>
          <li>Any course completion will guarantee employment or specific salary outcomes</li>
          <li>Information provided is always accurate, complete, or current</li>
          <li>Software will be free from bugs or technical issues</li>
        </List>
        <Paragraph>
          Your use of the Platform and Services is solely and entirely at your own risk and discretion. 
          You are required to independently assess and ensure that the Services meet your requirements.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>11. Limitation of Liability</SectionTitle>
        <Paragraph>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, {config.company.name.toUpperCase()}, ITS AFFILIATES, 
          OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS SHALL NOT BE LIABLE FOR:
        </Paragraph>
        <List>
          <li>Any indirect, incidental, special, consequential, or punitive damages</li>
          <li>Loss of profits, revenues, data, use, goodwill, or business opportunities</li>
          <li>Business interruption or loss of business</li>
          <li>Failure to achieve employment or career outcomes</li>
          <li>Technical failures, data loss, or system errors</li>
          <li>Unauthorized access to your account or data</li>
          <li>Actions or content of third parties</li>
        </List>
        <Paragraph>
          IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT PAID BY YOU FOR THE SERVICES IN THE 
          LAST 6 MONTHS OR RUPEES ONE THOUSAND (₹1,000), WHICHEVER IS LESS.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>12. Indemnification</SectionTitle>
        <Paragraph>
          You agree to indemnify, defend, and hold harmless {config.company.name}, its affiliates, and their 
          respective officers, directors, employees, agents, from any claim, demand, action, damage, loss, cost, 
          or expense (including reasonable attorneys' fees) arising out of or relating to:
        </Paragraph>
        <List>
          <li>Your use or misuse of the Platform or Services</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any rights of a third party, including intellectual property rights</li>
          <li>Your violation of any applicable law or regulation</li>
          <li>Any content or data you submit through the Platform</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>13. Privacy and Data Protection</SectionTitle>
        <Paragraph>
          Your use of the Platform is also governed by our Privacy Policy. By using our Services, you consent 
          to the collection, use, and sharing of your information as described in our Privacy Policy.
        </Paragraph>
        <Paragraph>
          We implement reasonable security measures but cannot guarantee absolute security. You are responsible 
          for maintaining the confidentiality of your account credentials.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>14. Third-Party Links and Services</SectionTitle>
        <Paragraph>
          The Platform may contain links to third-party websites, services, or resources. These links are provided 
          for your convenience only. We do not endorse and are not responsible for:
        </Paragraph>
        <List>
          <li>The content, accuracy, or availability of third-party sites</li>
          <li>Any products, services, or content available through third-party sites</li>
          <li>The privacy practices or terms of third-party services</li>
        </List>
        <Paragraph>
          Your use of third-party websites is at your own risk and subject to their terms and policies.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>15. Termination</SectionTitle>
        <Paragraph>
          We reserve the right to terminate or suspend your account and access to Services immediately, 
          without prior notice or liability, for any reason, including but not limited to:
        </Paragraph>
        <List>
          <li>Violation of these Terms</li>
          <li>Fraudulent or illegal activities</li>
          <li>Non-payment of fees</li>
          <li>Inappropriate behavior towards staff or other users</li>
          <li>Request from law enforcement or government authorities</li>
        </List>
        <Paragraph>
          Upon termination:
        </Paragraph>
        <List>
          <li>Your right to access the Platform and Services will cease immediately</li>
          <li>We may delete your account and data</li>
          <li>You remain liable for all outstanding payments</li>
          <li>Provisions regarding intellectual property, disclaimers, and limitations of liability survive termination</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>16. Force Majeure</SectionTitle>
        <Paragraph>
          We shall not be liable for any failure to perform our obligations under these Terms if such failure 
          is prevented or delayed by circumstances beyond our reasonable control, including but not limited to:
        </Paragraph>
        <List>
          <li>Acts of God, natural disasters, epidemics, pandemics</li>
          <li>War, terrorism, civil unrest, riots</li>
          <li>Government actions, laws, or regulations</li>
          <li>Internet service failures, power outages</li>
          <li>Strikes, labor disputes</li>
          <li>Third-party service failures (payment gateways, hosting providers)</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>17. Governing Law and Jurisdiction</SectionTitle>
        <Paragraph>
          These Terms and any disputes arising out of or relating to these Terms, the Platform, or Services 
          shall be governed by and construed in accordance with the laws of India, without regard to conflict 
          of law provisions.
        </Paragraph>
        <Paragraph>
          All disputes arising out of or in connection with these Terms shall be subject to the exclusive 
          jurisdiction of the courts in Bhandara, Maharashtra, India.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>18. Dispute Resolution</SectionTitle>
        <Paragraph>
          In the event of any dispute, controversy, or claim arising out of these Terms:
        </Paragraph>
        <List>
          <li>You agree to first contact us to attempt informal resolution</li>
          <li>If informal resolution fails, disputes shall be resolved through arbitration in Bhandara, Maharashtra</li>
          <li>Arbitration shall be conducted in accordance with the Arbitration and Conciliation Act, 1996</li>
          <li>The arbitration award shall be final and binding</li>
          <li>Each party shall bear their own costs unless otherwise decided by the arbitrator</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>19. Modifications to Terms</SectionTitle>
        <Paragraph>
          We reserve the right to modify, amend, or update these Terms at any time. Changes will be effective 
          immediately upon posting on the Platform. Material changes will be notified via email or prominent 
          notice on the Platform.
        </Paragraph>
        <Paragraph>
          Your continued use of the Platform or Services after any modifications constitutes your acceptance 
          of the revised Terms. If you do not agree to the modified Terms, you must stop using our Services.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>20. Severability</SectionTitle>
        <Paragraph>
          If any provision of these Terms is found to be invalid, illegal, or unenforceable, such provision 
          shall be modified to the minimum extent necessary to make it valid and enforceable. If modification 
          is not possible, the provision shall be severed, and the remaining provisions shall continue in full 
          force and effect.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>21. Entire Agreement</SectionTitle>
        <Paragraph>
          These Terms and Conditions, together with our Privacy Policy and any other policies referenced herein, 
          constitute the entire agreement between you and {config.company.name} regarding the use of the Platform 
          and Services, and supersede all prior agreements and understandings.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>22. Product-Specific Policies</SectionTitle>
        <Paragraph>
          For detailed policies specific to individual products or services, please refer to:
        </Paragraph>
        <List>
          <li><strong>Refund Policy:</strong> Detailed refund terms for all product categories</li>
          <li><strong>Return & Shipping Policy:</strong> Terms for physical product returns and shipping</li>
          <li><strong>Privacy Policy:</strong> How we collect, use, and protect your data</li>
          <li><strong>Product Documentation:</strong> User guides and terms for specific products</li>
          <li><strong>Service Level Agreements (SLA):</strong> Uptime and support commitments for SaaS products</li>
        </List>
        <Paragraph>
          Product-specific policies are available on our website or provided upon purchase. If there is any 
          conflict between these general Terms and product-specific policies, the product-specific policies 
          shall prevail for that particular product or service.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>23. Contact Information</SectionTitle>
        <Paragraph>
          For questions, concerns, or communications relating to these Terms and Conditions, please contact us:
        </Paragraph>
        <List>
          <li><strong>Company Name:</strong> {config.company.name}</li>
          <li><strong>Email:</strong> {config.company.email}</li>
          <li><strong>Phone:</strong> {config.company.phone}</li>
          <li><strong>Registered Address:</strong> {config.company.address}</li>
          <li><strong>Website:</strong> {config.company.website}</li>
        </List>
      </Section>

      <Section>
        <Paragraph style={{ marginTop: '40px', fontWeight: '600', color: '#333' }}>
          By using our Platform and Services, you acknowledge that you have read, understood, and agree to be 
          bound by these Terms and Conditions.
        </Paragraph>
      </Section>
    </PolicyContainer>
  );
};

export default TermsAndConditions;
